import { Box, Button, Divider, Flex, Group, Stack, Text } from '@mantine/core';
import { useDisclosure, useId } from '@mantine/hooks';
import { IconEdit as EditIcon } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { GovEvaluationAutomatedAnnexButton } from '@/module/GovEvaluation/components/GovEvaluationAutomatedAnnexButton';
import { GovEvaluationAuthorAnnexModalForm } from '@/module/GovEvaluation/form/GovEvaluationAuthorAnnexModalForm';
import {
  getGovEvaluationEditableAnnexDownloadUrl,
  getGovEvaluationSignedAnnexDownloadUrl,
} from '@/module/GovEvaluation/model/GovEvaluationApi';

import { Content } from '@/core/components';

import { FileCard, Id, useAppNotification } from '@feature/utils';

import { useGovEvaluationUploadAnnex } from '../model/GovEvaluationQuery';
import { GovAnnex, GovAuthorAnnex, GovEvalAuthorAnnexRequest } from '../model/GovEvaluationTypes';

export interface GovEvaluationAnnexItemProps {
  canEdit: boolean;
  annex: GovAnnex;
  authorAnnex: GovAuthorAnnex | undefined;

  evaluationId: Id;
  authorId: Id;
}

export function GovEvaluationAnnexItem(props: GovEvaluationAnnexItemProps) {
  const { annex, authorAnnex, canEdit, evaluationId, authorId } = props;
  const { t } = useTranslation();
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false);
  const { isPending, mutate } = useGovEvaluationUploadAnnex(evaluationId);
  const titleId = useId();

  const notification = useAppNotification();
  const onSubmit = (values: GovEvalAuthorAnnexRequest) => {
    mutate(values, {
      onSuccess: () => {
        notification.success();
        closeForm();
      },
      onError: notification.error,
    });
  };

  return (
    <Content bg="white">
      <Flex
        justify="space-between"
        align="center"
        role="group"
        aria-labelledby={titleId}
      >
        <Box>
          <Stack gap="3px">
            <Group gap="3px">
              <Text
                id={titleId}
                fw="500"
                size="sm"
              >
                {annex.name}
              </Text>
              {!annex.required && (
                <Text
                  c="dimmed"
                  size="sm"
                >
                  (opțional)
                </Text>
              )}
            </Group>
            <Text
              size="xs"
              c="dimmed"
            >
              {annex.description}
            </Text>
          </Stack>
          <Divider my="15px" />
          {!authorAnnex && <Text c="dimmed">{t('unspecified')}</Text>}
          {authorAnnex && (
            <Stack gap="xl">
              <Stack gap="2px">
                <Text
                  fw="500"
                  size="sm"
                >
                  {t('govEvaluation.signedPdfAnnex')}
                </Text>
                <FileCard
                  file={authorAnnex.signedProofFile}
                  onDownload={() =>
                    window.open(getGovEvaluationSignedAnnexDownloadUrl(evaluationId, authorId, annex.template))
                  }
                />
              </Stack>
              <Stack gap="2px">
                <Text
                  fw="500"
                  size="sm"
                >
                  {t('govEvaluation.editableXlsxAnnex')}
                </Text>
                <FileCard
                  file={authorAnnex.editableProofFile}
                  onDownload={() =>
                    window.open(getGovEvaluationEditableAnnexDownloadUrl(evaluationId, authorId, annex.template))
                  }
                />
              </Stack>
            </Stack>
          )}
        </Box>
        <Stack align="flex-end">
          <Button
            onClick={openForm}
            leftSection={<EditIcon size="14px" />}
            size="xs"
            disabled={!canEdit}
          >
            {t('button.edit')}
          </Button>
          {annex.automated && (
            <GovEvaluationAutomatedAnnexButton
              annexType={annex.template}
              evaluationId={evaluationId}
              authorId={authorId}
            />
          )}
        </Stack>
      </Flex>
      {canEdit && formOpened && (
        <GovEvaluationAuthorAnnexModalForm
          title={annex.name}
          onSubmit={onSubmit}
          isSubmitting={isPending}
          onClose={closeForm}
          type={annex.template}
        />
      )}
    </Content>
  );
}
